const API_URL = '/api';

const buildAuthHeaders = (base = {}) => {
  const headers = { ...base };

  try {
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      const parsed = JSON.parse(storedUser);
      if (parsed?.id) {
        headers['x-user-id'] = String(parsed.id);
      }
      if (parsed?.empresa_id) {
        headers['x-empresa-id'] = String(parsed.empresa_id);
      }
    }
  } catch (error) {
    console.warn('Não foi possível interpretar o usuário armazenado para os headers de autenticação:', error);
  }

  const token = localStorage.getItem('token');
  if (token && !headers.Authorization) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

export const api = {
  // Autenticação
  async login(email, senha) {
    const res = await fetch(`${API_URL}/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, senha }),
    });
    if (!res.ok) throw new Error('Erro no login');
    return res.json();
  },

  // Lançamentos
  async getLancamentos(params = {}) {
    // Compatibilidade com a assinatura antiga: getLancamentos(tipo, status)
    let query = params;
    if (arguments.length === 2) {
      query = { tipo: arguments[0], status: arguments[1] };
    } else if (typeof params === 'string' || Array.isArray(params)) {
      query = { tipo: params };
    } else if (params && typeof params === 'object' && ('tipo' in params || 'status' in params)) {
      query = { ...params };
    }

    const search = new URLSearchParams();
    Object.entries(query || {}).forEach(([key, value]) => {
      if (value === undefined || value === null || value === '') return;

      if (Array.isArray(value)) {
        value.forEach((item) => {
          if (item !== undefined && item !== null && item !== '') {
            search.append(key, item);
          }
        });
      } else {
        search.append(key, value);
      }
    });

    const qs = search.toString();
    const url = qs ? `${API_URL}/lancamentos?${qs}` : `${API_URL}/lancamentos`;

    const res = await fetch(url);
    if (!res.ok) throw new Error('Erro ao buscar lançamentos');
    return res.json();
  },

  async getLancamento(id) {
    const res = await fetch(`${API_URL}/lancamentos/${id}`);
    if (!res.ok) throw new Error('Erro ao buscar lançamento');
    return res.json();
  },

  async createLancamento(data) {
    const res = await fetch(`${API_URL}/lancamentos`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao criar lançamento');
    return res.json();
  },

  async updateLancamento(id, data) {
    const res = await fetch(`${API_URL}/lancamentos/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao atualizar lançamento');
    return res.json();
  },

  async deleteLancamento(id) {
    const res = await fetch(`${API_URL}/lancamentos/${id}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Erro ao deletar lançamento');
    return res.json();
  },

  // Notas
  async getNotas() {
    const res = await fetch(`${API_URL}/notas`);
    if (!res.ok) throw new Error('Erro ao buscar notas');
    return res.json();
  },

  async getNota(id) {
    const res = await fetch(`${API_URL}/notas/${id}`);
    if (!res.ok) throw new Error('Erro ao buscar nota');
    return res.json();
  },

  async createNota(data) {
    const res = await fetch(`${API_URL}/notas`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Erro ao criar nota');
    }
    return res.json();
  },

  async updateNota(id, data) {
    const res = await fetch(`${API_URL}/notas/${id}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Erro ao atualizar nota');
    }
    return res.json();
  },

  async deleteNota(id) {
    const res = await fetch(`${API_URL}/notas/${id}`, { method: 'DELETE' });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Erro ao deletar nota');
    }
    return res.json();
  },

  // Configurações
  async getEmpresaConfig() {
    const res = await fetch(`${API_URL}/configuracoes/empresa`, {
      headers: buildAuthHeaders(),
    });
    if (!res.ok) throw new Error('Erro ao buscar dados da empresa');
    return res.json();
  },

  async saveEmpresaConfig(data) {
    const res = await fetch(`${API_URL}/configuracoes/empresa`, {
      method: 'PUT',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Erro ao salvar dados da empresa');
    }
    return res.json();
  },

  // Dashboard
  async getDashboardStats(params = {}) {
    const search = new URLSearchParams();
    Object.entries(params || {}).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        search.append(key, value);
      }
    });
    const qs = search.toString();
    const url = qs ? `${API_URL}/dashboard/stats?${qs}` : `${API_URL}/dashboard/stats`;
    const res = await fetch(url);
    if (!res.ok) throw new Error('Falha ao carregar estatísticas');
    return res.json();
  },

  async getDashboardPreferences() {
    const res = await fetch(`${API_URL}/dashboard/preferences`, {
      headers: buildAuthHeaders(),
    });
    if (!res.ok) throw new Error('Falha ao carregar preferências do dashboard');
    return res.json();
  },

  async saveDashboardPreferences(preferences = {}) {
    const res = await fetch(`${API_URL}/dashboard/preferences`, {
      method: 'PUT',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ preferences }),
    });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Falha ao salvar preferências do dashboard');
    }
    return res.json();
  },

  // Usuários
  async getUsuarios() {
    const res = await fetch(`${API_URL}/usuarios`, {
      headers: buildAuthHeaders(),
    });
    if (!res.ok) throw new Error('Erro ao buscar usuários');
    return res.json();
  },

  async createUsuario(data) {
    const res = await fetch(`${API_URL}/usuarios`, {
      method: 'POST',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!res.ok) throw new Error('Erro ao criar usuário');
    return res.json();
  },

  // Notificações
  async getNotificationRules() {
    const res = await fetch(`${API_URL}/notifications`, {
      headers: buildAuthHeaders(),
    });
    if (!res.ok) throw new Error('Erro ao carregar regras de notificação');
    return res.json();
  },

  async createNotificationRule(data) {
    const res = await fetch(`${API_URL}/notifications`, {
      method: 'POST',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Erro ao criar regra de notificação');
    }
    return res.json();
  },

  async updateNotificationRule(id, data) {
    const res = await fetch(`${API_URL}/notifications/${id}`, {
      method: 'PUT',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify(data),
    });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Erro ao atualizar regra de notificação');
    }
    return res.json();
  },

  async deleteNotificationRule(id) {
    const res = await fetch(`${API_URL}/notifications/${id}`, {
      method: 'DELETE',
      headers: buildAuthHeaders(),
    });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Erro ao remover regra de notificação');
    }
    return res.json();
  },

  async getPendingNotifications() {
    const res = await fetch(`${API_URL}/notifications/pending`, {
      headers: buildAuthHeaders(),
    });
    if (!res.ok) {
      throw new Error('Erro ao buscar notificações pendentes');
    }
    return res.json();
  },

  async markNotificationAsRead(id) {
    const res = await fetch(`${API_URL}/notifications/events/${id}`, {
      method: 'PUT',
      headers: buildAuthHeaders({ 'Content-Type': 'application/json' }),
      body: JSON.stringify({ status: 'read' }),
    });
    if (!res.ok) {
      let body = null; try { body = await res.json(); } catch { /* ignore */ }
      throw new Error(body?.error || 'Erro ao atualizar notificação');
    }
    return res.json();
  },
};
